import type { Apartment, ApartmentStats } from "@/models/responses";
import type { StatFilter } from "@/hooks/useAggregatedStats";

/**
 * One per (group × month) row in the aggregated output.
 *
 * Additive metrics (`grossIncome`, `netIncome`, `daysBooked`,
 * `availableBookingDays`, `daysBlocked`, `numberOfVisits`,
 * `numberOfReservationsReceived`, `leadDays`) are summed over the apartments
 * in the group at the given month that match every active predicate.
 *
 * Weighted-average metrics are computed via sum-of-numerator /
 * sum-of-denominator from those summed totals — matching the convention
 * already in use in `bookingPaceSlice.ts:161-167` and
 * `pages/dashboards/blt.tsx:84-85`. When the denominator is 0, the result is
 * 0 (NEVER NaN) so chart libraries don't bail out on the value.
 *
 * `monthIndex` is the index into the original `months` array — callers can
 * use it to reconstruct the month label / Date.
 */
export interface AggregatedGroupStats {
    groupId: number;
    grossIncome: number;
    netIncome: number;
    daysBooked: number;
    availableBookingDays: number;
    daysBlocked: number;
    numberOfVisits: number;
    numberOfReservationsReceived: number;
    leadDays: number;
    grossAdr: number;
    netAdr: number;
    occupancy: number;
    averageBlt: number;
    averageLengthOfStay: number;
    monthIndex: number;
}

/**
 * Aggregate per-apartment monthly stats into per-group monthly stats.
 *
 * Rules:
 *  - Apartments whose `apartmentEntities[id]` is undefined are dropped under
 *    any active filter (matches Tier 1 semantics from
 *    `apartment-filters-everywhere`).
 *  - Predicates are AND'd: an apartment contributes only if every predicate
 *    returns `true` for it.
 *  - A group is OMITTED entirely from the output when its filter-matching
 *    apartment set is empty across ALL months. Months where some other group
 *    contributes get a row for that group; months where this group has no
 *    surviving apartments simply don't get a row for this group (keeps the
 *    chart's data-transform consumer happy — it accumulates per-month rows
 *    keyed by group name and only paints groups that appear).
 *  - Division-by-zero produces `0`, never `NaN`.
 *
 * The input shape mirrors the `/api/admin/apartments/stats-by-month`
 * response: `apartmentStats` is a flat array indexed as
 * `apartments × months`, in month-major order — for each month index `m`,
 * stats `[m * numberOfApartments .. (m+1) * numberOfApartments - 1]` are the
 * stats for that month, one per apartment in the order the backend returned
 * them. The helper iterates the array in that order using
 * `Math.floor(index / numberOfApartments)` → month index, mirroring the
 * existing transform in `apartment-graphs.tsx`.
 */
export function aggregateApartmentStatsByGroup(
    apartmentStats: ApartmentStats[],
    months: Date[],
    apartmentEntities: Record<number, Apartment>,
    predicates: StatFilter[],
): AggregatedGroupStats[] {
    if (months.length === 0 || apartmentStats.length === 0) {
        return [];
    }

    const numberOfApartments = Math.floor(apartmentStats.length / months.length);
    if (numberOfApartments === 0) {
        return [];
    }

    // Pass 1: collect surviving (apartmentId, monthIndex) pairs after applying
    // predicates. Apartments whose entity is missing are excluded.
    interface Bucket {
        groupId: number;
        grossIncome: number;
        netIncome: number;
        daysBooked: number;
        availableBookingDays: number;
        daysBlocked: number;
        numberOfVisits: number;
        numberOfReservationsReceived: number;
        leadDays: number;
    }

    // Map<monthIndex, Map<groupId, Bucket>>
    const byMonth = new Map<number, Map<number, Bucket>>();
    // Track which groups had ANY contribution across all months so we can
    // skip emitting empty rows for groups that never made it past the filter.
    const groupsWithAnyContribution = new Set<number>();

    apartmentStats.forEach((stat, index) => {
        const monthIndex = Math.floor(index / numberOfApartments);
        if (monthIndex >= months.length) {
            return; // Defensive: extra rows beyond declared months are ignored.
        }
        const apartment = apartmentEntities[stat.apartmentId];
        if (apartment === undefined) {
            return;
        }
        if (!predicates.every((predicate) => predicate(apartment))) {
            return;
        }

        let perGroup = byMonth.get(monthIndex);
        if (perGroup === undefined) {
            perGroup = new Map<number, Bucket>();
            byMonth.set(monthIndex, perGroup);
        }

        let bucket = perGroup.get(apartment.groupId);
        if (bucket === undefined) {
            bucket = {
                groupId: apartment.groupId,
                grossIncome: 0,
                netIncome: 0,
                daysBooked: 0,
                availableBookingDays: 0,
                daysBlocked: 0,
                numberOfVisits: 0,
                numberOfReservationsReceived: 0,
                leadDays: 0,
            };
            perGroup.set(apartment.groupId, bucket);
        }

        bucket.grossIncome += stat.grossIncome;
        bucket.netIncome += stat.netIncome;
        bucket.daysBooked += stat.daysBooked;
        bucket.availableBookingDays += stat.availableBookingDays;
        bucket.daysBlocked += stat.daysBlocked;
        bucket.numberOfVisits += stat.numberOfVisits;
        bucket.numberOfReservationsReceived += stat.numberOfReservationsReceived;
        bucket.leadDays += stat.leadDays ?? 0;

        groupsWithAnyContribution.add(apartment.groupId);
    });

    // Pass 2: flatten + derive weighted averages, guarding against div-by-0.
    const out: AggregatedGroupStats[] = [];
    byMonth.forEach((perGroup, monthIndex) => {
        perGroup.forEach((bucket) => {
            if (!groupsWithAnyContribution.has(bucket.groupId)) {
                return;
            }
            out.push({
                groupId: bucket.groupId,
                grossIncome: bucket.grossIncome,
                netIncome: bucket.netIncome,
                daysBooked: bucket.daysBooked,
                availableBookingDays: bucket.availableBookingDays,
                daysBlocked: bucket.daysBlocked,
                numberOfVisits: bucket.numberOfVisits,
                numberOfReservationsReceived: bucket.numberOfReservationsReceived,
                leadDays: bucket.leadDays,
                grossAdr: bucket.daysBooked > 0 ? bucket.grossIncome / bucket.daysBooked : 0,
                netAdr: bucket.daysBooked > 0 ? bucket.netIncome / bucket.daysBooked : 0,
                occupancy:
                    bucket.availableBookingDays > bucket.daysBlocked
                        ? (100 * bucket.daysBooked) / (bucket.availableBookingDays - bucket.daysBlocked)
                        : 0,
                averageBlt: bucket.numberOfVisits > 0 ? bucket.leadDays / bucket.numberOfVisits : 0,
                averageLengthOfStay: bucket.numberOfVisits > 0 ? bucket.daysBooked / bucket.numberOfVisits : 0,
                monthIndex,
            });
        });
    });

    return out;
}
